import { useEffect, useState, useRef } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";

interface TextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
  fontName: string;
}

interface PdfTextLayerProps {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  zoom: number;
  rotation: number;
}

export function PdfTextLayer({ pdf, pageNumber, zoom, rotation }: PdfTextLayerProps) {
  const [textItems, setTextItems] = useState<TextItem[]>([]);
  const [viewport, setViewport] = useState<{ width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadTextContent() {
      try {
        const page = await pdf.getPage(pageNumber);
        const vp = page.getViewport({ scale: 1, rotation });
        const textContent = await page.getTextContent();

        if (cancelled) return;

        setViewport({ width: vp.width, height: vp.height });
        setTextItems(textContent.items as TextItem[]);
      } catch (err) {
        console.error("Failed to load text content:", err);
      }
    }

    loadTextContent();
    return () => {
      cancelled = true;
    };
  }, [pdf, pageNumber, rotation]);

  if (!viewport || textItems.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="pdf-text-layer absolute inset-0 overflow-hidden"
      style={{
        width: viewport.width * zoom,
        height: viewport.height * zoom,
      }}
    >
      {textItems.map((item, index) => {
        if (!item.str.trim()) return null;

        const tx = item.transform;
        // tx[0] = scaleX, tx[1] = skewY, tx[2] = skewX, tx[3] = scaleY, tx[4] = translateX, tx[5] = translateY
        const x = tx[4] * zoom;
        const y = (viewport.height - tx[5]) * zoom;
        const fontSize = Math.sqrt(tx[0] * tx[0] + tx[1] * tx[1]) * zoom;
        const width = item.width * zoom;

        return (
          <span
            key={index}
            className="pdf-text-item"
            style={{
              left: x,
              top: y - fontSize,
              fontSize: `${fontSize}px`,
              fontFamily: "sans-serif",
              width: width > 0 ? width : undefined,
              transform: tx[0] < 0 ? "scaleX(-1)" : undefined,
            }}
          >
            {item.str}
          </span>
        );
      })}
    </div>
  );
}
